import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { currencyType } from '@trashlab/api/src/domains/core/schemas/base-template';
import { PgAddress } from '@trashlab/api/src/domains/core/types';
import { driver } from '@trashlab/api/src/domains/driver/schemas';
import { sql } from 'drizzle-orm';
import {
  AnyPgColumn,
  boolean,
  jsonb,
  doublePrecision,
  foreignKey,
  index,
  integer,
  numeric,
  smallint,
  pgEnum,
  pgTable,
  text,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';

/**
 * Enum defining the accounting method used by the organization.
 * - CASH: Revenue/expenses recorded when cash changes hands
 * - ACCRUAL: Revenue/expenses recorded when earned/incurred
 */
export const accounting_basis = pgEnum('accounting_basis', ['CASH', 'ACCRUAL']);

/**
 * Enum defining how strictly assets are tracked.
 * - STRICT: Rigorous tracking of all asset movements
 * - LOOSE: More flexible tracking with fewer restrictions
 */
export const asset_tracking_preference = pgEnum('asset_tracking_preference', [
  'STRICT',
  'LOOSE',
]);

/**
 * Enum defining the country where the organization is located.
 * Used for country-specific logic like tax handling.
 */
export const organization_country = pgEnum('organization_country', [
  'USA',
  'CANADA',
]);

/**
 * Enum listing all US state abbreviations.
 * Used for address validation and state-specific logic.
 */
export const us_state = pgEnum('us_state', [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]);

/**
 * Enum defining how tax breakdowns appear on invoices.
 * - COMPONENTS: Show individual tax components
 * - SUMMARY: Show only total tax amount
 */
export const invoice_tax_breakdown_preference = pgEnum(
  'invoice_tax_breakdown_preference',
  ['COMPONENTS', 'SUMMARY'],
);

/**
 * Enum defining legal business entity types.
 */
export const business_type = pgEnum('business_type', [
  'Partnership',
  'Limited Liability Corporation',
  'Co-operative',
  'Non-profit Corporation',
  'Corporation',
]);

/**
 * Enum defining high-level company categories.
 */
export const company_type = pgEnum('company_type', [
  'government',
  'non_profit',
  'private',
]);

/**
 * Enum defining organization status for metrics purposes.
 * Not used for business logic.
 */
export const tenant_status = pgEnum('tenant_status', [
  'ACTIVE',
  'INACTIVE',
  'ACTIVE_TEST',
]);

/**
 * Configuration for standard fees applied across the organization.
 * Defines default fee names and settings.
 */
export const standard_fee_config = pgTable(
  'standard_fee_config',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Display text for haul fees */
    dumpsterFeeCopy: text('dumpsterFeeCopy').default('Haul Fee').notNull(),
    /** Display text for tonnage overage fees (PROPORTIONAL rounding mode) */
    tonnageFeeCopy: text('tonnageFeeCopy').default('Tonnage').notNull(),
    /** Display text for the minimum tonnage fee when overage falls below the minimum threshold */
    minimumTonnageFeeCopy: text('minimumTonnageFeeCopy').default('Minimum Tonnage Fee').notNull(),
    /** Display text for tonnage overage fees (ROUND_UP rounding mode) */
    roundedUpTonnageFeeCopy: text('roundedUpTonnageFeeCopy').default('Tonnage').notNull(),
    /**
     * When enabled (default), tonnage overage line items append the dynamic
     * rounding breakdown after the label (e.g. "(rounded up from 0.57t to 1t)")
     * and populate the line item details. When disabled, only the label shows.
     */
    showTonnageBreakdownNote: boolean('showTonnageBreakdownNote').default(true).notNull(),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
  },
  (table) => {
    return {
      createdAtTs_idx: index('standard_fee_config_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('standard_fee_config_tenantId_idx').on(
        table.tenantId,
      ),
      updatedAtTs_idx: index('standard_fee_config_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('standard_fee_config_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('standard_fee_config_versionNumber_idx').on(
        table.versionNumber,
      ),
    };
  },
);

/**
 * Historical record of changes to standard fee configurations.
 */
export const standard_fee_config_history = pgTable(
  'standard_fee_config_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    dumpsterFeeCopy: text('dumpsterFeeCopy'),
    tonnageFeeCopy: text('tonnageFeeCopy'),
    minimumTonnageFeeCopy: text('minimumTonnageFeeCopy'),
    roundedUpTonnageFeeCopy: text('roundedUpTonnageFeeCopy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
  },
);

/**
 * Represents a waste management organization in the system.
 * Contains organization settings, preferences, and business information.
 * Core entity that ties together users, sites, and operational configurations.
 */
export const organization = pgTable(
  'organization',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** Organization's active status */
    status: tenant_status('status').default('ACTIVE').notNull(),
    /** Internal identifier for the organization */
    internalUniqueId: varchar('internalUniqueId', { length: 255 }).unique(),
    /** Organization's legal name */
    name: varchar('name', { length: 255 }).notNull(),
    /** Structured address information */
    address: jsonb('address').$type<PgAddress>(),
    /** Full address as a single string */
    addressString: text('addressString'),
    /** Email for billing communications */
    billingEmail: varchar('billingEmail', { length: 256 }),
    /** Custom from email for outbound billing communications (invoices, payment receipts) */
    sendFromBillingEmail: varchar('sendFromBillingEmail', { length: 256 }),
    /** CC email for billing communications - if set, will be CCed on invoices and payment receipts */
    billingCCEmail: varchar('billingCCEmail', { length: 256 }),
    /** Phone number for billing communications */
    billingPhoneNumber: varchar('billingPhoneNumber', { length: 24 }),
    /** Phone number for dispatch communications */
    dispatchPhoneNumber: varchar('dispatchPhoneNumber', { length: 24 }),
    /** Payment facilitator ID */
    payfacId: varchar('payfacId'),
    /** Employer Identification Number */
    ein: varchar('ein', { length: 24 }),
    /** Organization's timezone for scheduling */
    timezone: varchar('timezone', { length: 64 })
      .default(sql`'America/Los_Angeles'::character varying`)
      .notNull(),
    /** How strictly assets are tracked */
    assetTrackingPreference: asset_tracking_preference(
      'assetTrackingPreference',
    )
      .default('STRICT')
      .notNull(),
    /** Whether this is a test organization */
    isTestOrg: boolean('isTestOrg').default(false).notNull(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Path to organization's logo image */
    logoPath: varchar('logoPath'),
    /** Whether driver notifications are enabled */
    driverNotificationsPreference: boolean(
      'driverNotificationsPreference',
    ).default(true),
    /** Whether to use site-level prefixes on invoices */
    invoiceSiteLevelPrefixPreference: boolean(
      'invoiceSiteLevelPrefixPreference',
    ).default(false),
    /** Whether to allow non-sequential order numbers */
    nonSequentialOrderNumberPreference: boolean(
      'nonSequentialOrderNumberPreference',
    ).default(false),
    /** Whether QuickBooks integration is enabled */
    quickbooksEnabled: boolean('quickbooksEnabled').default(false).notNull(),
    /** Reference to standard fee configuration */
    standardFeeConfigId: uuid('standardFeeConfigId'),
    /** Whether email communications are disabled */
    disableEmails: boolean('disableEmails').default(false).notNull(),
    /** Whether text communications are disabled */
    disableTexts: boolean('disableTexts').default(false).notNull(),
    /** Whether ACH payments are enabled */
    enableAchPaymentsPreference: boolean('enableAchPaymentsPreference')
      .default(true)
      .notNull(),
    /** Default memo text for invoices */
    defaultInvoiceMemo: text('defaultInvoiceMemo'),
    /** Days between past due invoice reminders */
    pastDueInvoiceReminderDaysInterval: integer(
      'pastDueInvoiceReminderDaysInterval',
    ).default(28),
    /** Whether automatic sales tax calculation is enabled */
    automaticSalesTaxEnabled: boolean('automaticSalesTaxEnabled')
      .default(true)
      .notNull(),
    /** Organization's accounting method */
    accountingBasis: accounting_basis('accountingBasis')
      .default('ACCRUAL')
      .notNull(),
    useDbPaymentsSnapshot: boolean('useDbPaymentsSnapshot')
      .default(false)
      .notNull(),
    /** URL for online order thank you page */
    onlineOrderThankYouPageUrl: varchar('onlineOrderThankYouPageUrl', {
      length: 256,
    }),
    /** Google Tag Manager ID for online orders */
    onlineOrderGoogleTagManagerId: varchar('onlineOrderGoogleTagManagerId', {
      length: 256,
    }),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Whether time tracking feature is enabled */
    enableTimeTrackingFlag: boolean('enableTimeTrackingFlag')
      .default(true)
      .notNull(),
    /** How tax breakdowns appear on invoices */
    invoiceTaxBreakdownPreference: invoice_tax_breakdown_preference(
      'invoiceTaxBreakdownPreference',
    )
      .default('COMPONENTS')
      .notNull(),
    /** Credit card surcharge percentage for online orders */
    onlineOrderCreditCardSurchargePercentage: doublePrecision(
      'onlineOrderCreditCardSurchargePercentage',
    )
      .default(0)
      .notNull(),
    /** ACH surcharge percentage for online orders */
    onlineOrderAchSurchargePercentage: doublePrecision(
      'onlineOrderAchSurchargePercentage',
    )
      .default(0)
      .notNull(),
    /** Day of week when time tracking week ends (0-6) */
    timeTrackingEndOfWeek: integer('timeTrackingEndOfWeek')
      .default(6)
      .notNull(),
    /** Day of week when time tracking week starts (0-6) */
    timeTrackingStartOfWeek: integer('timeTrackingStartOfWeek')
      .default(0)
      .notNull(),
    /** Default prefix for invoice numbers */
    defaultInvoicePrefix: varchar('defaultInvoicePrefix', { length: 8 })
      .default(sql`'INV'::character varying`)
      .notNull(),
    /** Whether driver vehicle inspection reports are enabled */
    dvirEnabled: boolean('dvirEnabled').default(false).notNull(),
    /** Whether custom DVIR forms are enabled */
    dvirCustomEnabled: boolean('dvirCustomEnabled').default(true).notNull(),
    /** Whether communications center is enabled */
    commsCenterEnabled: boolean('commsCenterEnabled').default(false).notNull(),
    /** Whether AI capabilities are enabled in the contact center */
    aiContactCenterEnabled: boolean('aiContactCenterEnabled')
      .default(false)
      .notNull(),
    /** Version of online shop being used */
    onlineShopVersion: varchar('onlineShopVersion', { length: 8 })
      .default(sql`'V3'::character varying`)
      .notNull(),
    /** Whether bulk billing feature is enabled */
    enableBulkBillingFlag: boolean('enableBulkBillingFlag')
      .default(true)
      .notNull(),
    /** Whether bulk dispatch feature is enabled */
    enableBulkDispatchFlag: boolean('enableBulkDispatchFlag')
      .default(true)
      .notNull(),
    /** URL for online shop */
    website: varchar('website', { length: 256 }),
    /** URL for privacy policy */
    privacyPolicyUrl: varchar('privacyPolicyUrl', { length: 256 }),
    /** Append the customer account aging breakdown to invoice PDFs and emails */
    showInvoiceAccountBalance: boolean('showInvoiceAccountBalance')
      .default(false)
      .notNull(),
    /** URL for terms and conditions */
    termsAndConditionsUrl: varchar('termsAndConditionsUrl', { length: 256 }),
    /** Route driver taps on phone numbers in order notes through Comms Center */
    orderNoteCallsUseCommsCenter: boolean('orderNoteCallsUseCommsCenter')
      .default(true)
      .notNull(),
    /** Legal business entity type */
    businessType: business_type('businessType'),
    /** High-level company category */
    companyType: company_type('companyType'),
    /** Whether to collate invoice charges */
    collateInvoiceCharges: boolean('collateInvoiceCharges')
      .default(false)
      .notNull(),
    /** Hide customer email on invoice bill-to section */
    hideInvoiceBillToEmail: boolean('hideInvoiceBillToEmail')
      .default(false)
      .notNull(),
    /** Hide customer phone on invoice bill-to section */
    hideInvoiceBillToPhone: boolean('hideInvoiceBillToPhone')
      .default(false)
      .notNull(),
    /** Service holds if balance exceeds */
    serviceHoldsIfBalanceExceeds: numeric('serviceHoldsIfBalanceExceeds'),
    /** New: Credit limit applied to past-due balance only */
    creditLimitPastDue: numeric('creditLimitPastDue'),
    /** New: Credit limit applied to open balance */
    creditLimitOpen: numeric('creditLimitOpen'),
    /** Default driver ID */
    defaultDriverId: uuid('defaultDriverId').references(
      (): AnyPgColumn => driver.id,
    ),
    /** Whether QB Conductor is enabled */
    qbConductorEnabled: boolean('qbConductorEnabled').default(false).notNull(),
    /** Whether driver photos are required */
    requireDriverPhotos: boolean('requireDriverPhotos').default(true).notNull(),
    currency: currencyType('currency').default('usd').notNull(),
    enableNativeWebCalling: boolean('enableNativeWebCalling')
      .default(true)
      .notNull(),
    aiPhoneCallEnabled: boolean('aiPhoneCallEnabled').default(false).notNull(),
    aiSmsEnabled: boolean('aiSmsEnabled').default(false).notNull(),
    /** Country where the organization is based */
    country: organization_country('country').default('USA').notNull(),
    websiteCustomizationEnabled: boolean('websiteCustomizationEnabled')
      .default(true)
      .notNull(),
    turnByTurnNavigationEnabled: boolean('turnByTurnNavigationEnabled')
      .default(true)
      .notNull(),
    /** the TwiML App's pointed at this URL in tests */
    twilioWebhooksUrl: varchar('twilioWebhooksUrl', { length: 256 }),
    /** Whether subscription feature is enabled */
    subscriptionEnabled: boolean('subscriptionEnabled').default(true).notNull(),
    receiveInvoiceCopy: boolean('receiveInvoiceCopy').default(true).notNull(),
    /** Timeout for contact lease in comms center */
    contactLeaseTimeout: integer('contactLeaseTimeout'),
    requestsEnabled: boolean('requestsEnabled').default(false).notNull(),
    /** Whether scale module is enabled */
    scaleModuleEnabled: boolean('scaleModuleEnabled').default(false).notNull(),
    callPopupPipEnabled: boolean('callPopupPipEnabled')
      .default(false)
      .notNull(),
    /** Whether both parties consent to the call */
    bothPartiesConsent: boolean('bothPartiesConsent').default(true).notNull(),
    /** Flag to enable native order creation from mobile app */
    enableMobileNativeOrderCreation: boolean('enableMobileNativeOrderCreation')
      .default(false)
      .notNull(),
    enableInternalComms: boolean('enableInternalComms')
      .default(false)
      .notNull(),
    /** Whether PDFmake statement generation is enabled */
    enablePdfmakeStatements: boolean('enablePdfmakeStatements')
      .default(false)
      .notNull(),
    /** Whether to auto-send order tracking link on order creation */
    autoSendOrderTrackingLinkPreference: boolean(
      'autoSendOrderTrackingLinkPreference',
    )
      .default(false)
      .notNull(),
    /** Feature flag: Use V2 recurring configurations selection flow */
    recurringConfigsV2Enabled: boolean('recurringConfigsV2Enabled')
      .default(true)
      .notNull(),
    /** Default copies for scale ticket printing across the org */
    defaultScaleTicketCopiesPreference: varchar(
      'defaultScaleTicketCopiesPreference',
      { length: 16 },
    )
      .default('TRIPLE')
      .notNull(),
    /** Allow drivers to create charges through the mobile app */
    driverChargeCreationEnabled: boolean('driverChargeCreationEnabled')
      .default(false)
      .notNull(),
    /** Enable self-service backup exports for enterprise customers */
    backupExportsEnabled: boolean('backupExportsEnabled')
      .default(false)
      .notNull(),
    /** Whether broker integrations are enabled */
    brokerIntegrationsEnabled: boolean('brokerIntegrationsEnabled')
      .default(false)
      .notNull(),
    /** Enable order-level time tracking for time-based billing */
    enableOrderLevelTimeTracking: boolean('enableOrderLevelTimeTracking')
      .default(false)
      .notNull(),
    /** Whether AI order taker feature is enabled */
    enableAiOrderTaker: boolean('enableAiOrderTaker').default(false).notNull(),
    /** Whether deals/pipeline CRM feature is enabled */
    pipelineEnabled: boolean('pipelineEnabled').default(false).notNull(),
    /** Whether drivers can reassign themselves to a different schedule via mobile app */
    allowDriverReassigningSchedule: boolean('allowDriverReassigningSchedule')
      .default(false)
      .notNull(),
    broadcastBrandColor: varchar('broadcastBrandColor', { length: 7 }).default('#276749'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('organization_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('organization_tenantId_idx').on(table.tenantId),
      consoleCommsNameTrigram: index(
        'organization_console_comms_name_trgm_idx',
      ).using('gin', table.name.op('gin_trgm_ops')),
      updatedAtTs_idx: index('organization_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('organization_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('organization_versionNumber_idx').on(
        table.versionNumber,
      ),
      defaultDriverId_idx: index('organization_defaultDriverId_idx').on(
        table.defaultDriverId,
      ),
      standardFeeConfigId_idx: index('organization_standardFeeConfigId_idx').on(
        table.standardFeeConfigId,
      ),
      activeNameCKeysetIdx: index('organization_active_name_c_keyset_idx')
        .on(sql`LOWER(${table.name}) COLLATE "C"`, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      organization_standardFeeConfigId_fkey: foreignKey({
        columns: [table.standardFeeConfigId],
        foreignColumns: [standard_fee_config.id],
        name: 'organization_standardFeeConfigId_fkey',
      }),
    };
  },
);

/**
 * Historical record of changes to organizations.
 * Tracks all modifications to organization records over time for auditing and versioning.
 * Contains the same fields as organization but allows nulls for partial updates.
 */
export const organization_history = pgTable('organization_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  status: tenant_status('status'),
  internalUniqueId: varchar('internalUniqueId', { length: 255 }),
  name: varchar('name', { length: 255 }),
  address: jsonb('address').$type<PgAddress>(),
  addressString: text('addressString'),
  billingEmail: varchar('billingEmail', { length: 256 }),
  sendFromBillingEmail: varchar('sendFromBillingEmail', { length: 256 }),
  billingCCEmail: varchar('billingCCEmail', { length: 256 }),
  billingPhoneNumber: varchar('billingPhoneNumber', { length: 24 }),
  dispatchPhoneNumber: varchar('dispatchPhoneNumber', { length: 24 }),
  payfacId: varchar('payfacId'),
  ein: varchar('ein', { length: 24 }),
  timezone: varchar('timezone', { length: 64 }),
  assetTrackingPreference: asset_tracking_preference('assetTrackingPreference'),
  isTestOrg: boolean('isTestOrg'),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  logoPath: varchar('logoPath'),
  driverNotificationsPreference: boolean('driverNotificationsPreference'),
  invoiceSiteLevelPrefixPreference: boolean('invoiceSiteLevelPrefixPreference'),
  nonSequentialOrderNumberPreference: boolean(
    'nonSequentialOrderNumberPreference',
  ),
  quickbooksEnabled: boolean('quickbooksEnabled'),
  standardFeeConfigId: uuid('standardFeeConfigId'),
  disableEmails: boolean('disableEmails'),
  disableTexts: boolean('disableTexts'),
  enableAchPaymentsPreference: boolean('enableAchPaymentsPreference'),
  defaultInvoiceMemo: text('defaultInvoiceMemo'),
  pastDueInvoiceReminderDaysInterval: integer(
    'pastDueInvoiceReminderDaysInterval',
  ),
  automaticSalesTaxEnabled: boolean('automaticSalesTaxEnabled'),
  accountingBasis: accounting_basis('accountingBasis'),
  useDbPaymentsSnapshot: boolean('useDbPaymentsSnapshot'),
  onlineOrderThankYouPageUrl: varchar('onlineOrderThankYouPageUrl', {
    length: 256,
  }),
  onlineOrderGoogleTagManagerId: varchar('onlineOrderGoogleTagManagerId', {
    length: 256,
  }),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  enableTimeTrackingFlag: boolean('enableTimeTrackingFlag'),
  invoiceTaxBreakdownPreference: invoice_tax_breakdown_preference(
    'invoiceTaxBreakdownPreference',
  ),
  onlineOrderCreditCardSurchargePercentage: doublePrecision(
    'onlineOrderCreditCardSurchargePercentage',
  ),
  onlineOrderAchSurchargePercentage: doublePrecision(
    'onlineOrderAchSurchargePercentage',
  ),
  timeTrackingEndOfWeek: integer('timeTrackingEndOfWeek'),
  timeTrackingStartOfWeek: integer('timeTrackingStartOfWeek'),
  defaultInvoicePrefix: varchar('defaultInvoicePrefix', { length: 8 }),
  dvirEnabled: boolean('dvirEnabled'),
  dvirCustomEnabled: boolean('dvirCustomEnabled'),
  commsCenterEnabled: boolean('commsCenterEnabled'),
  aiContactCenterEnabled: boolean('aiContactCenterEnabled'),
  onlineShopVersion: varchar('onlineShopVersion', { length: 8 }),
  enableBulkBillingFlag: boolean('enableBulkBillingFlag'),
  enableBulkDispatchFlag: boolean('enableBulkDispatchFlag'),
  website: varchar('website', { length: 256 }),
  privacyPolicyUrl: varchar('privacyPolicyUrl', { length: 256 }),
  showInvoiceAccountBalance: boolean('showInvoiceAccountBalance'),
  termsAndConditionsUrl: varchar('termsAndConditionsUrl', { length: 256 }),
  orderNoteCallsUseCommsCenter: boolean('orderNoteCallsUseCommsCenter'),
  businessType: business_type('businessType'),
  companyType: company_type('companyType'),
  collateInvoiceCharges: boolean('collateInvoiceCharges'),
  hideInvoiceBillToEmail: boolean('hideInvoiceBillToEmail'),
  hideInvoiceBillToPhone: boolean('hideInvoiceBillToPhone'),
  serviceHoldsIfBalanceExceeds: numeric('serviceHoldsIfBalanceExceeds'),
  creditLimitPastDue: numeric('creditLimitPastDue'),
  creditLimitOpen: numeric('creditLimitOpen'),
  defaultDriverId: uuid('defaultDriverId'),
  qbConductorEnabled: boolean('qbConductorEnabled'),
  requireDriverPhotos: boolean('requireDriverPhotos'),
  currency: currencyType('currency'),
  enableNativeWebCalling: boolean('enableNativeWebCalling').default(false),
  aiPhoneCallEnabled: boolean('aiPhoneCallEnabled'),
  aiSmsEnabled: boolean('aiSmsEnabled'),
  country: organization_country('country'),
  websiteCustomizationEnabled: boolean('websiteCustomizationEnabled'),
  turnByTurnNavigationEnabled: boolean('turnByTurnNavigationEnabled'),
  twilioWebhooksUrl: varchar('twilioWebhooksUrl', { length: 256 }),
  subscriptionEnabled: boolean('subscriptionEnabled'),
  receiveInvoiceCopy: boolean('receiveInvoiceCopy'),
  contactLeaseTimeout: integer('contactLeaseTimeout'),
  requestsEnabled: boolean('requestsEnabled'),
  scaleModuleEnabled: boolean('scaleModuleEnabled'),
  callPopupPipEnabled: boolean('callPopupPipEnabled'),
  bothPartiesConsent: boolean('bothPartiesConsent'),
  enableMobileNativeOrderCreation: boolean('enableMobileNativeOrderCreation'),
  enableInternalComms: boolean('enableInternalComms'),
  enablePdfmakeStatements: boolean('enablePdfmakeStatements'),
  autoSendOrderTrackingLinkPreference: boolean(
    'autoSendOrderTrackingLinkPreference',
  ),
  recurringConfigsV2Enabled: boolean('recurringConfigsV2Enabled').default(true),
  defaultScaleTicketCopiesPreference: varchar(
    'defaultScaleTicketCopiesPreference',
    { length: 16 },
  ),
  driverChargeCreationEnabled: boolean('driverChargeCreationEnabled'),
  backupExportsEnabled: boolean('backupExportsEnabled'),
  brokerIntegrationsEnabled: boolean('brokerIntegrationsEnabled'),
  enableOrderLevelTimeTracking: boolean('enableOrderLevelTimeTracking'),
  enableAiOrderTaker: boolean('enableAiOrderTaker'),
  pipelineEnabled: boolean('pipelineEnabled'),
  allowDriverReassigningSchedule: boolean('allowDriverReassigningSchedule'),
  broadcastBrandColor: varchar('broadcastBrandColor', { length: 7 }),
});

/** Type inference for organization schema */
export type OrganizationSchema = typeof organization.$inferSelect;
